import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IndianRupee, Plus, Users } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useStaffSession } from '@/lib/auth';
import { leadKeys, useTemplates, type Template } from '@/lib/leads';
import { MoreList, Page, PageHeader } from '@/components/Shell';
import { Empty, LoadError, SectionLabel } from '@/components/ui';

/* ------------------------------------------- places that arrive in later phases */

function Coming({
  title,
  icon: Icon,
  phase,
  children,
}: {
  title: string;
  icon: typeof Users;
  phase: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <PageHeader title={title} />
      <Page>
        <div className="max-w-xl">
          <Icon className="size-6 text-fg-faint" aria-hidden strokeWidth={1.5} />
          <p className="mt-3 text-lg font-semibold">Arrives in {phase}.</p>
          <div className="mt-2 grid gap-3 text-fg-dim">{children}</div>
          <p className="mt-6 text-sm text-fg-faint">The full plan is in docs/ADMIN_PLAN.md.</p>
        </div>
      </Page>
    </>
  );
}

export function ClientsScreen() {
  return (
    <Coming title="Clients" icon={Users} phase="phase 4, with briefs">
      <p>
        Each client gets one page: contacts, files, projects, money and a single timeline. Winning an enquiry turns it
        into a client with one tap.
      </p>
      <p>
        Briefs are the centrepiece. You send one link, the client fills a checklist made for their kind of job and
        uploads everything, and it lands in <code className="num text-xs">AgencyProjects/&lt;Business&gt;/client-assets</code>.
      </p>
    </Coming>
  );
}

export function MoneyScreen() {
  return (
    <Coming title="Money" icon={IndianRupee} phase="phase 5">
      <p>
        Quotes the client accepts with one tap, then invoices with financial-year numbers, a UPI QR and bank details,
        sent as a PDF and a link. Payments recorded as they land, and reminders drafted for you to send.
      </p>
    </Coming>
  );
}

export function MoreScreen() {
  return (
    <>
      <PageHeader title="More" />
      <Page>
        <MoreList />
      </Page>
    </>
  );
}

/* ---------------------------------------------------------------- settings */

export function SettingsScreen() {
  return (
    <>
      <PageHeader title="Settings" />
      <Page>
        <div className="grid max-w-3xl gap-10">
          <Account />
          <SavedReplies />
        </div>
      </Page>
    </>
  );
}

function Account() {
  const session = useStaffSession();
  return (
    <section aria-labelledby="acct">
      <SectionLabel>
        <span id="acct">Your account</span>
      </SectionLabel>
      <div className="grid gap-3 rounded-lg border border-edge bg-bg-raised p-4 text-sm">
        <p>
          Signed in as <span className="font-semibold">{session.user.email}</span>
        </p>
        <p className="text-fg-dim">
          To change your password, use Authentication in the Supabase dashboard. Desk never handles passwords.
        </p>
      </div>
    </section>
  );
}

function SavedReplies() {
  const templates = useTemplates();
  const [adding, setAdding] = useState<'whatsapp' | 'email' | null>(null);

  return (
    <section aria-labelledby="replies">
      <SectionLabel aside="{{first_name}} {{name}} {{company}} {{service}} {{date}}">
        <span id="replies">Saved replies</span>
      </SectionLabel>
      <p className="mb-4 text-sm text-fg-dim">
        The starting messages in every WhatsApp and email composer. A variable with no value becomes a neutral phrase,
        so a message never goes out with a gap in it.
      </p>
      {templates.error ? (
        <LoadError error={templates.error} retry={() => templates.refetch()} />
      ) : templates.isPending ? (
        <div className="skeleton h-40 w-full" />
      ) : (
        <div className="grid gap-8">
          {(['whatsapp', 'email'] as const).map((channel) => {
            const list = templates.data.filter((t) => t.channel === channel);
            return (
              <div key={channel}>
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-xs font-semibold tracking-wide text-fg-dim uppercase">
                    {channel === 'whatsapp' ? 'WhatsApp' : 'Email'}
                  </h3>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAdding(channel)}>
                    <Plus className="size-4" aria-hidden /> Add
                  </button>
                </div>
                {list.length === 0 && adding !== channel && (
                  <Empty title="No saved replies">Add one and it appears in the composer.</Empty>
                )}
                <div className="grid gap-3">
                  {adding === channel && <TemplateEditor channel={channel} onDone={() => setAdding(null)} />}
                  {list.map((t) => (
                    <TemplateEditor key={t.id} template={t} channel={channel} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function TemplateEditor({
  template,
  channel,
  onDone,
}: {
  template?: Template;
  channel: 'whatsapp' | 'email';
  onDone?: () => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(!template);
  const [name, setName] = useState(template?.name ?? '');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [body, setBody] = useState(template?.body ?? '');

  const save = useMutation({
    mutationFn: async (archive?: boolean) => {
      const row = { channel, name: name.trim(), subject: channel === 'email' ? subject.trim() : null, body };
      const { error } = template
        ? await supabase
            .from('message_templates')
            .update(archive ? { archived: true } : row)
            .eq('id', template.id)
        : await supabase.from('message_templates').insert({ ...row, position: 100 });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: leadKeys.templates });
      setOpen(false);
      onDone?.();
    },
  });

  if (!open && template) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="grid gap-1 rounded-lg border border-edge bg-bg-raised px-4 py-3 text-left transition-colors hover:border-edge-strong"
      >
        <span className="font-semibold">{template.name}</span>
        <span className="line-clamp-2 text-sm text-fg-dim">{template.subject ? `${template.subject} — ` : ''}{template.body}</span>
      </button>
    );
  }

  const valid = name.trim() && body.trim() && (channel === 'whatsapp' || subject.trim());
  return (
    <div className="grid gap-3 rounded-lg border border-edge-strong bg-bg-raised p-4">
      <input className="input" placeholder="Name, e.g. First reply" value={name} onChange={(e) => setName(e.target.value)} aria-label="Name" />
      {channel === 'email' && (
        <input className="input" placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} aria-label="Subject" />
      )}
      <textarea className="input" rows={channel === 'email' ? 8 : 4} value={body} onChange={(e) => setBody(e.target.value)} aria-label="Message" />
      {save.error && <p className="error-text">{save.error.message}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn btn-ink btn-sm" disabled={!valid || save.isPending} onClick={() => save.mutate(false)}>
          Save
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => {
            setOpen(false);
            onDone?.();
          }}
        >
          Cancel
        </button>
        {template && (
          <button type="button" className="btn btn-ghost btn-sm ml-auto text-alert" onClick={() => save.mutate(true)}>
            Remove
          </button>
        )}
      </div>
    </div>
  );
}
